import { z } from "zod";
import { ContractPartySchema } from "@remi/enums/contract-party";
import { PandadocDocumentTypeSchema } from "@remi/enums/pandadoc-document-type";
import { TaskType } from "@remi/enums/task-type";
import { EscrowSchema } from "@remi/types/escrow";
import { FinancingSchema } from "@remi/types/financing";
import { PandadocDocumentSchema } from "@remi/types/pandadocDocument";
import { UpdateHomeownerSchema } from "@remi/types/person";
import {
  PrevRoofMaterialTypeSchema,
  RoofMaterialTypeDisplay,
  RoofMaterialTypeSchema,
  SupportedRoofMaterial,
} from "@remi/types/roofMaterial";
import { RoofScope, RoofScopeDisplayNames } from "@remi/types/roofScope";
import { UpsellReasonSchema } from "@remi/types/upsell";
import { SiteAccessInputSchema } from "@remi/types/worksite";

import { TaskAttemptType } from "./attempt-types";

/** Explicitly map selected materials to a roof material type, or roof scope */
export const RecordUpsellResultsNewMaterialOptions = {
  [SupportedRoofMaterial.ASPHALT]: RoofMaterialTypeDisplay[SupportedRoofMaterial.ASPHALT],
  [RoofScope.ROLLED_COMP]: RoofScopeDisplayNames[RoofScope.ROLLED_COMP],
  [SupportedRoofMaterial.ASPHALT_AND_NEW_TILE]: RoofMaterialTypeDisplay[SupportedRoofMaterial.ASPHALT_AND_NEW_TILE],
  [SupportedRoofMaterial.ASPHALT_AND_ORIGINAL_TILE]:
    RoofMaterialTypeDisplay[SupportedRoofMaterial.ASPHALT_AND_ORIGINAL_TILE],
  [SupportedRoofMaterial.EPDM]: RoofMaterialTypeDisplay[SupportedRoofMaterial.EPDM],
  [SupportedRoofMaterial.OTHER]: RoofMaterialTypeDisplay[SupportedRoofMaterial.OTHER],
  [SupportedRoofMaterial.THREE_TAB_SHINGLE]: RoofMaterialTypeDisplay[SupportedRoofMaterial.THREE_TAB_SHINGLE],
  [SupportedRoofMaterial.TILE_REPLACE_WITH_NEW]: RoofMaterialTypeDisplay[SupportedRoofMaterial.TILE_REPLACE_WITH_NEW],
  [SupportedRoofMaterial.TILE_RESET_ORIGINAL_CLAY]:
    RoofMaterialTypeDisplay[SupportedRoofMaterial.TILE_RESET_ORIGINAL_CLAY],
  [SupportedRoofMaterial.TILE_RESET_ORIGINAL_CONCRETE]:
    RoofMaterialTypeDisplay[SupportedRoofMaterial.TILE_RESET_ORIGINAL_CONCRETE],
  [SupportedRoofMaterial.TPO]: RoofMaterialTypeDisplay[SupportedRoofMaterial.TPO],
  [SupportedRoofMaterial.PRESIDENTIAL_SHINGLE]: RoofMaterialTypeDisplay[SupportedRoofMaterial.PRESIDENTIAL_SHINGLE],
  [SupportedRoofMaterial.CLASS_4_SHINGLE]: RoofMaterialTypeDisplay[SupportedRoofMaterial.CLASS_4_SHINGLE],
  [SupportedRoofMaterial.UNKNOWN]: RoofMaterialTypeDisplay[SupportedRoofMaterial.UNKNOWN],
} as const;

const RecordUpsellResultsSchema = z.object({
  attemptType: z.literal(TaskAttemptType.ATTEMPT),
  quoteVersionId: z.string(),
  apr: z.number(),
  termMonths: z.number(),
  loanAmount: z.number(),
  productType: z.string().max(50).optional(),
  documentType: PandadocDocumentTypeSchema.optional(),
  bidData: z.object({
    upsellSuccessful: z.boolean(),
    upsellNotes: z.string().optional(),
    upsellCompetitor: z.string().optional(),
    upsellCompetitorBid: z.number().optional(),
    upsellReason: UpsellReasonSchema.optional(),
    contractParty: ContractPartySchema,
    externalContractorName: z.string().nullable().optional(),
    externalContractorContactName: z.string().nullable().optional(),
    externalContractorPhone: z.string().nullable().optional(),
    externalContractorRoofWorkStartDate: z.coerce.date().nullable().optional(),
  }),
  workSite: SiteAccessInputSchema.extend(UpdateHomeownerSchema.pick({ preferredContactMethod: true }).shape).optional(),
  homeownerInsurance: z
    .object({
      involvesInsuranceClaim: z.boolean().nullable().optional(),
      homeownerPolicyNumber: z.string().nullable().optional(),
      homeownerInsuranceCompany: z.string().nullable().optional(),
    })
    .optional(),
  materials: z
    .object({
      colorSelection: z.string().optional(),
      dripEdgeColorSelection: z.string().optional(),
      shingleLine: z.string().trim().min(1).max(191).optional(),
      shingleColor: z.string().max(191).optional(),
      dripEdgeColor: z.string().max(191).optional(),
      dripEdgeColorSourcingConfirmed: z.boolean().optional(),
      satelliteNotes: z.string().optional(),
      numberOfLayers: z.number().optional(),
      currentRoofMaterials: z.array(PrevRoofMaterialTypeSchema).optional(),
      newRoofMaterials: z.array(RoofMaterialTypeSchema).optional(),
      hasRolledComp: z.boolean().optional(),
    })
    .optional(),
});

export type RecordUpsellResultsInput = z.infer<typeof RecordUpsellResultsSchema>;

const RecordUpsellResultsTaskAttemptFormDataSchema = RecordUpsellResultsSchema.extend({
  attemptType: z.literal(TaskAttemptType.ATTEMPT),
});

const RecordUpsellResultsTaskRequestFormDataSchema = z.object({
  quoteVersionId: z.string(),
  sowChange: z.string(),
  attemptType: z.literal(TaskAttemptType.REQUEST),
});

export const RecordUpsellResultsTaskHistoryFormSchema = z.object({
  type: z.literal(TaskType.RECORD_UPSELL_RESULTS),
  payload: z.discriminatedUnion("attemptType", [
    RecordUpsellResultsTaskAttemptFormDataSchema,
    RecordUpsellResultsTaskRequestFormDataSchema,
  ]),
});

const RecordUpsellResultsTaskAttemptSchema = RecordUpsellResultsTaskAttemptFormDataSchema.extend({
  financing: FinancingSchema.nullish(),
  escrow: EscrowSchema.nullish(),
  pandadocDocument: PandadocDocumentSchema.nullish(),
});

export const RecordUpsellResultsTaskHistorySchema = z.object({
  type: z.literal(TaskType.RECORD_UPSELL_RESULTS),
  payload: z.discriminatedUnion("attemptType", [
    RecordUpsellResultsTaskAttemptFormDataSchema.extend({
      financing: FinancingSchema.nullish(),
      escrow: EscrowSchema.nullish(),
      pandadocDocument: PandadocDocumentSchema.nullish(),
    }),
    RecordUpsellResultsTaskRequestFormDataSchema,
  ]),
});

export type RecordUpsellResultsTaskAttempt = z.infer<typeof RecordUpsellResultsTaskAttemptSchema>;
export type RecordUpsellResultsTaskAttemptFormData = z.infer<typeof RecordUpsellResultsTaskAttemptFormDataSchema>;
export type RecordUpsellResultsTaskRequestFormData = z.infer<typeof RecordUpsellResultsTaskRequestFormDataSchema>;
